import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Wallet } from "lucide-react";

interface Account {
  id: string;
  name: string;
  type: string;
  currency: string;
  balance: number;
}

export interface PortfolioSummaryCardProps {
  totalNetWorth: number;
  baseCurrency: string;
  selectedCurrency?: string;
  accountsByType: Record<string, Account[]>;
  isLoading: boolean;
  isConverting?: boolean;
}

const accountTypeLabels: Record<string, string> = {
  bank: "Bank Accounts",
  mobile_money: "Mobile Money",
  card: "Cards",
  cash: "Cash",
  savings: "Savings",
  investment: "Investments",
};

export function PortfolioSummaryCard({
  totalNetWorth,
  baseCurrency,
  selectedCurrency,
  accountsByType,
  isLoading,
  isConverting = false,
}: PortfolioSummaryCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Portfolio Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayCurrency = selectedCurrency;
  const isCurrencyConverted = selectedCurrency !== baseCurrency;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
          Portfolio Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6" aria-busy={isConverting}>
        {/* Total Net Worth */}
        <section aria-labelledby="net-worth-heading">
          <h3 id="net-worth-heading" className="text-xs sm:text-sm text-muted-foreground">Total Net Worth</h3>
          <div className="flex items-baseline gap-2 flex-wrap">
            {isConverting ? (
              <Skeleton className="h-10 w-40" />
            ) : (
              <>
                <p className="text-2xl sm:text-3xl font-bold break-all" aria-label={`Total net worth: ${new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: displayCurrency,
                }).format(totalNetWorth)}`}>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: displayCurrency,
                  }).format(totalNetWorth)}
                </p>
                <Badge variant="secondary" className="text-xs" aria-label={`Currency: ${displayCurrency}`}>
                  {displayCurrency}
                </Badge>
              </>
            )}
          </div>
        </section>

        {/* Breakdown by Account Type */}
        <section aria-labelledby="breakdown-heading">
          <h3 id="breakdown-heading" className="text-xs sm:text-sm font-medium">Breakdown by Type</h3>
          <div className="space-y-2" role="list">
            {isConverting ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))
            ) : (
              Object.entries(accountsByType).map(([type, accounts]) => {
                const typeTotal = accounts.reduce((sum, account) => sum + account.balance, 0);
                const percentage = totalNetWorth > 0 
                  ? ((typeTotal / totalNetWorth) * 100).toFixed(1)
                  : '0.0';

                return (
                  <div
                    key={type}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    role="listitem"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Wallet className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium truncate">
                          {accountTypeLabels[type] || type}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {accounts.length} {accounts.length === 1 ? 'account' : 'accounts'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-xs sm:text-sm font-semibold break-all">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: displayCurrency,
                        }).format(typeTotal)}
                      </p>
                      <p className="text-xs text-muted-foreground">{percentage}%</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Currency Conversion Indicator */}
        {!isConverting && isCurrencyConverted && (
          <div className="pt-2 border-t">
            <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap" role="note">
              <Badge variant="outline" className="text-xs">
                Converted
              </Badge>
              <span>Values converted from {baseCurrency} to {displayCurrency}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
